import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  MenuItem,
  Select,
  Chip,
  FormControl,
  InputLabel,
  OutlinedInput
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CityService from '../../../services/city.service';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '12px', 
      minWidth: '500px'
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '24px'
  },
  actions: {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  },
  submitButton: {
    backgroundColor: 'primary.main',
    borderRadius: '8px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#333'
    }
  },
  cancelButton: {
    color: '#666',
    borderRadius: '8px',
    textTransform: 'none'
  }
};

const statusOptions = [
  { value: 'non publié', label: 'Non publié' },
  { value: 'publié', label: 'Publié' },
  { value: 'fermé', label: 'Fermé' }
];

function Form({ open, onClose, initialData = null, onSubmit }) {
  const [cities, setCities] = React.useState([]);
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    date_start_sales: '',
    date_end_sales: '',
    date_start_event: '',
    date_end_event: '',
    status: 'non publié',
    cities: []
  });

  React.useEffect(() => {
    const fetchCities = async () => {
      try {
        const citiesData = await CityService.list();
        setCities(citiesData);
      } catch (error) {
        console.error('Erreur lors du chargement des villes:', error);
      }
    };
    fetchCities();
  }, []);

  React.useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        date_start_sales: initialData.date_start_sales?.split('T')[0] || '',
        date_end_sales: initialData.date_end_sales?.split('T')[0] || '',
        date_start_event: initialData.date_start_event?.split('T')[0] || '',
        date_end_event: initialData.date_end_event?.split('T')[0] || '',
        status: initialData.status || 'non publié',
        cities: initialData.cities?.map(city => city.id) || []
      });
    } else {
      setFormData({
        name: '',
        description: '',
        date_start_sales: '',
        date_end_sales: '',
        date_start_event: '',
        date_end_event: '',
        status: 'non publié',
        cities: []
      });
    }
  }, [initialData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'cities') {
      setFormData(prev => ({
        ...prev,
        cities: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (onSubmit) {
        onSubmit(formData);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      date_start_sales: '',
      date_end_sales: '',
      date_start_event: '',
      date_end_event: '',
      status: 'non publié',
      cities: []
    });
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      sx={styles.dialog}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <span>{initialData ? 'Modifier l\'événement' : 'Nouvel événement'}</span>
        <IconButton 
          onClick={handleClose}
          size="small"
          sx={{ color: '#666' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={styles.form}>
          <TextField
            fullWidth
            label="Nom"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />
          
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            multiline
            rows={4}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              sx={{
                width: { xs: '100%', md: '48%' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
              label="Début des achats"
              name="date_start_sales" 
              type="date"
              value={formData.date_start_sales}
              onChange={handleChange}
              required
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              sx={{
                width: { xs: '100%', md: '48%' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
              label="Fin des achats"
              name="date_end_sales"
              type="date"
              value={formData.date_end_sales}
              onChange={handleChange}
              required
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              sx={{
                width: { xs: '100%', md: '48%' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
              label="Début de l'événement"
              name="date_start_event"
              type="date"
              value={formData.date_start_event}
              onChange={handleChange}
              required
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              sx={{
                width: { xs: '100%', md: '48%' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
              label="Fin de l'événement"
              name="date_end_event"
              type="date"
              value={formData.date_end_event}
              onChange={handleChange}
              required
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <TextField
            fullWidth
            select
            label="Statut"
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <FormControl fullWidth size="small">
            <InputLabel id="cities-label">Villes</InputLabel>
            <Select
              labelId="cities-label"
              multiple
              name="cities"
              value={formData.cities}
              onChange={handleChange}
              input={<OutlinedInput label="Villes" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((cityId) => {
                    const city = cities.find(c => c.id === cityId);
                    return <Chip key={cityId} label={city?.name} />;
                  })}
                </Box>
              )}
              sx={{
                borderRadius: '8px'
              }}
            >
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions sx={styles.actions}>
          <Button 
            onClick={handleClose}
            sx={styles.cancelButton}
          >
            Annuler
          </Button>
          <Button 
            type="submit"
            variant="contained"
            sx={styles.submitButton}
          >
            {initialData ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

Form.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  initialData: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    date_start_sales: PropTypes.string,
    date_end_sales: PropTypes.string,
    date_start_event: PropTypes.string,
    date_end_event: PropTypes.string,
    status: PropTypes.string,
    cities: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }))
  })
};

export default Form;